import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalAuth } from '../../lib/portal-api';
import { usePortalAuth } from '../../store/portal-auth';

export default function PortalLoginPage() {
  const navigate = useNavigate();
  const { setCustomer } = usePortalAuth();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [epost, setEpost] = useState('');
  const [kode, setKode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!epost.trim()) return;
    setLoading(true); setError(null);
    try {
      await portalAuth.sendCode(epost.trim());
      setInfo('Vi har sendt en 6-sifret kode til din e-post.');
      setStep('code');
    } catch (err: any) { setError(err?.message ?? 'Kunne ikke sende kode.'); }
    finally { setLoading(false); }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (kode.length !== 6) return;
    setLoading(true); setError(null);
    try {
      const result = await portalAuth.verify(epost.trim(), kode);
      setCustomer(result.customer);
      navigate('/portal');
    } catch (err: any) { setError(err?.message ?? 'Ugyldig kode.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-brand shadow-glow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6 text-white"><path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7" /><path d="M3 7l2-4h14l2 4" /><path d="M3 7h18" /></svg>
          </div>
          <h1 className="text-xl font-semibold text-slate-800">Kundeportal</h1>
          <p className="mt-1 text-sm text-slate-500">Logg inn med engangskode via e-post</p>
        </div>
        <div className="card p-6">
          {error && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          {info && step === 'code' && <div className="mb-4 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">{info}</div>}
          {step === 'email' ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div><label className="mb-1 block text-sm font-medium text-slate-700">E-post</label><input type="email" required value={epost} onChange={(e) => setEpost(e.target.value)} placeholder="din@epost.no" className="input" autoFocus /></div>
              <button type="submit" disabled={loading || !epost.trim()} className="btn-primary w-full">{loading ? 'Sender kode…' : 'Send innloggingskode'}</button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div><label className="mb-1 block text-sm font-medium text-slate-700">Engangskode (6 siffer)</label><input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={kode} onChange={(e) => setKode(e.target.value.replace(/\D/g, ''))} placeholder="000000" className="input text-center text-2xl tracking-[0.5em]" autoFocus /></div>
              <button type="submit" disabled={loading || kode.length !== 6} className="btn-primary w-full">{loading ? 'Verifiserer…' : 'Logg inn'}</button>
              <button type="button" onClick={() => { setStep('email'); setKode(''); setError(null); setInfo(null); }} className="btn-ghost w-full text-sm">Tilbake</button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-slate-400">Har du ikke konto? Ta kontakt med support.</p>
      </div>
    </div>
  );
}
